/**
 * Modale "Envoyer un email".
 *
 * Destinataire, Objet, Modèle, Corps modifiable, pièce jointe optionnelle.
 */
import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import {
  X, Mail, Paperclip, Send, FileText,
} from 'lucide-react';
import { useClientActions } from './ClientActionsContext';
import { useToast } from '../../../hooks/use-toast';
import { buildSuggestedMessage, daysSince } from './shared';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMAIL_TEMPLATES = [
  { id: 'inactive', label: 'Relance client inactif', subject: 'Nous pensons à vous — Baobab Connect' },
  { id: 'commercial', label: 'Proposition commerciale', subject: 'Notre offre transport pour {{client}}' },
  { id: 'feedback', label: 'Demande de feedback', subject: 'Votre avis nous intéresse' },
];

export default function EmailModal() {
  const { modal, client, close } = useClientActions();
  const { toast } = useToast();
  const fileRef = useRef(null);
  const isOpen = modal === 'email' && !!client;

  const [template, setTemplate] = useState('inactive');
  const [subject, setSubject] = useState(EMAIL_TEMPLATES[0].subject);
  const [body, setBody] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);

  React.useEffect(() => {
    if (client) setBody(buildSuggestedMessage(client.companyName, daysSince(client.lastSeen)));
  }, [client]);

  React.useEffect(() => {
    const t = EMAIL_TEMPLATES.find((x) => x.id === template);
    if (t && client) {
      setSubject(t.subject.replace('{{client}}', client.companyName));
    }
  }, [template, client]);

  if (!isOpen) return null;

  const handlePick = (e) => {
    const f = e.target.files?.[0];
    if (f) setAttachment(f);
  };

  const handleSend = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/admin/customer-success/actions`, {
        companyName: client.companyName,
        clientEmail: client.primaryEmail,
        action: 'email',
        channel: 'email',
        subject,
        body,
        visibility: 'commercial',
        metadata: { template, attachmentName: attachment?.name },
      });
      toast({ title: 'Email envoyé', description: `Email envoyé à ${client.primaryEmail || client.companyName}.` });
      close();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden flex flex-col" data-testid="email-modal">
        <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Mail className="w-4 h-4 text-emerald-700" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Envoyer un email</h2>
              <p className="text-xs text-gray-500">Destinataire : {client.primaryEmail || '—'}</p>
            </div>
          </div>
          <button onClick={close} className="text-gray-400 hover:text-gray-600" data-testid="close-email-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 pb-4 space-y-4 overflow-auto">
          <div>
            <label className="text-xs text-gray-500 block mb-1">À</label>
            <Input value={client.primaryEmail || ''} readOnly className="bg-gray-50" data-testid="email-to" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Modèle</label>
            <Select value={template} onValueChange={setTemplate}>
              <SelectTrigger data-testid="email-template-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EMAIL_TEMPLATES.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Objet</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Message</label>
            <Textarea
              rows={9} value={body} onChange={(e) => setBody(e.target.value)}
              className="font-mono text-xs" data-testid="email-body"
            />
          </div>
          {/* Pièce jointe */}
          <div>
            <input ref={fileRef} type="file" hidden onChange={handlePick} data-testid="email-file-input" />
            {attachment ? (
              <div className="flex items-center gap-2 text-xs bg-gray-50 border border-gray-100 rounded-md px-3 py-2">
                <FileText className="w-3.5 h-3.5 text-gray-500" />
                <span className="flex-1 truncate">{attachment.name}</span>
                <button onClick={() => setAttachment(null)} className="text-red-500 hover:text-red-600 text-[10px]">
                  Retirer
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileRef.current?.click()}
                className="text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1.5"
                data-testid="email-attach-btn"
              >
                <Paperclip className="w-3.5 h-3.5" /> Joindre un fichier
              </button>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={close} data-testid="email-cancel-btn">
            Annuler
          </Button>
          <Button
            size="sm" onClick={handleSend}
            disabled={!subject.trim() || !body.trim() || sending}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="email-send-btn"
          >
            <Send className="w-3.5 h-3.5 mr-1.5" />
            {sending ? 'Envoi...' : "Envoyer l'email"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
